/**
 * Standalone article fetcher for react-router.config.ts prerender.
 * Must NOT import from files that use @/ aliases or Jotai atoms,
 * because react-router.config.ts runs in plain Node before Vite aliases are available.
 */

import { getArticleUrl } from "./slug.js";

interface ArticleListEntry {
  filename: string;
  dateModified?: string;
}

interface ArticleMinimal {
  title: string;
  ingress: string;
  date: string;
  mainImage: { url: string };
}

function normalizeManifest(raw: unknown): ArticleListEntry[] {
  if (!Array.isArray(raw)) {
    throw new Error("Article manifest must be an array");
  }
  const entries: ArticleListEntry[] = [];
  for (let i = 0; i < raw.length; i++) {
    const item = raw[i];
    if (typeof item === "string") {
      entries.push({ filename: item });
      continue;
    }
    if (item != null && typeof item === "object" && !Array.isArray(item)) {
      const candidate = item as { filename?: unknown; dateModified?: unknown };
      if (typeof candidate.filename !== "string") {
        throw new Error(
          `Article manifest item at index ${i} must have a string filename`
        );
      }
      if (
        candidate.dateModified != null &&
        typeof candidate.dateModified !== "string"
      ) {
        throw new Error(
          `Article manifest item at index ${i} must have a string dateModified when provided`
        );
      }
      entries.push({
        filename: candidate.filename,
        dateModified:
          typeof candidate.dateModified === "string"
            ? candidate.dateModified
            : undefined
      });
      continue;
    }
    throw new Error(
      `Article manifest item at index ${i} must be a string or an object with a string filename`
    );
  }
  return entries;
}

const RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 200;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Retries transient fetch failures (network errors or non-OK responses) with a
// short linear backoff. The build prerenders dozens of blog pages that hit the
// same bucket, so an occasional blip must not fail a request on the first try
// and silently leave a page without its content.
const fetchWithRetry = async (
  url: string,
  attempts: number = RETRY_ATTEMPTS
): Promise<Response> => {
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const res = await fetch(url);
      if (res.ok) return res;
      lastError = new Error(`Request failed: ${res.status} for ${url}`);
    } catch (error) {
      lastError = error;
    }
    if (attempt < attempts) await delay(RETRY_BASE_DELAY_MS * attempt);
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(`Request failed for ${url}`);
};

const fetchManifest = async (
  bucketUrl: string,
  articlesPath: string,
  listFilename: string
): Promise<ArticleListEntry[]> => {
  const listUrl = `${bucketUrl}${articlesPath}${listFilename}`;
  const res = await fetchWithRetry(listUrl);
  const raw: unknown = await res.json();
  return normalizeManifest(raw);
};

export interface BlogArticleFetchOptions {
  /**
   * When true (default), per-article fetch failures throw so build-time callers
   * fail loudly. When false, failed articles are omitted (runtime fallback).
   */
  strict?: boolean;
}

const fetchJson = async <T>(
  url: string,
  { strict = true }: BlogArticleFetchOptions = {}
): Promise<T | null> => {
  try {
    const r = await fetchWithRetry(url);
    return (await r.json()) as T;
  } catch (error) {
    if (strict) {
      throw error;
    }
    return null;
  }
};

interface ArticleWithEntry<T> {
  entry: ArticleListEntry;
  article: T;
}

// Resolves the manifest and fetches every listed article in parallel. The
// manifest `entry` is kept alongside each article so callers can read
// manifest-level metadata (e.g. `dateModified`). In strict mode (default) any
// per-article failure rejects; in non-strict mode failed articles are dropped.
const fetchArticlesWithEntries = async <T>(
  bucketUrl: string,
  articlesPath: string,
  listFilename: string,
  options: BlogArticleFetchOptions = {}
): Promise<ArticleWithEntry<T>[]> => {
  const { strict = true } = options;
  const entries = await fetchManifest(bucketUrl, articlesPath, listFilename);
  const articlesUrl = `${bucketUrl}${articlesPath}`;
  const results = await Promise.all(
    entries.map(async (entry): Promise<ArticleWithEntry<T> | null> => {
      const article = await fetchJson<T>(
        `${articlesUrl}${entry.filename}`,
        options
      );
      if (!article) {
        if (strict) {
          throw new Error(
            `Article data missing or empty: ${entry.filename} (${articlesUrl}${entry.filename})`
          );
        }
        console.warn(
          `Skipping article ${entry.filename} (${articlesUrl}${entry.filename})`
        );
        return null;
      }
      return { entry, article };
    })
  );
  return results.filter((r): r is ArticleWithEntry<T> => r !== null);
};

export interface FetchedArticle {
  title: string;
  ingress: string;
  date: string;
  dateModified?: string;
  mainImage: { url: string; caption?: string; kind?: "ArticleImage" };
  body: unknown[];
  faqItemList?: number[];
  articleBody?: string;
  ctaAfterIngress?: { text: string; href: string };
  ctaAfterBody?: { text: string; href: string };
}

export async function fetchAllArticles<T = FetchedArticle>(
  bucketUrl: string,
  articlesPath: string,
  listFilename: string,
  options?: BlogArticleFetchOptions
): Promise<{ filename: string; article: T }[]> {
  const pairs = await fetchArticlesWithEntries<T>(
    bucketUrl,
    articlesPath,
    listFilename,
    options
  );
  return pairs.map(({ entry, article }) => ({
    filename: entry.filename,
    article
  }));
}

export interface ArticleSitemapEntry {
  slug: string;
  // W3C date (YYYY-MM-DD) for <lastmod>; omitted when no valid date is known.
  lastmod?: string;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Resolves a slug plus a sitemap `lastmod` per article, preferring the
 * manifest's `dateModified` and falling back to the article's publish `date`.
 * Only well-formed YYYY-MM-DD values are kept. Powers both the prerender path
 * list and the sitemap, so they always derive from one fetch.
 */
export async function fetchArticleSitemapEntries(
  bucketUrl: string,
  articlesPath: string,
  listFilename: string,
  options?: BlogArticleFetchOptions
): Promise<ArticleSitemapEntry[]> {
  const pairs = await fetchArticlesWithEntries<ArticleMinimal>(
    bucketUrl,
    articlesPath,
    listFilename,
    options
  );
  return pairs.map(({ entry, article }) => {
    const candidate = entry.dateModified ?? article.date;
    const lastmod =
      candidate && ISO_DATE_RE.test(candidate) ? candidate : undefined;
    return { slug: getArticleUrl(article.title), lastmod };
  });
}
